import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  FilterUserParameter,
  IAddNewUser,
  IAddNewUserResponse,
  ICriteria,
  IFilter,
  IGroupUser,
  IShareAccess,
  IUpdateUserGroupAccess,
  IUser,
  IUserGroupDetails,
  IUserGroupUpdateDetails,
  IUserSearch,
  SharedUser,
} from './user-management-interfaces';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { StateManagementService } from '../shared/services/state-management.service';
import { ERROR_CODES } from '../shared/constants/error-codes';
import { Utility } from '../shared/utils/utility.service';
import { userTypeResponse } from '../shared/constants/response-mapping';
import { IDefaultUserGroup } from '../shared-layout/Components/activity/user-card.interface';
import { CONSTANTS } from '../shared/constants/constants';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private url = environment.graphqlUrl;
  private resetForm = new BehaviorSubject<boolean>(false);
  public triggerRerender = new BehaviorSubject<boolean>(false);
  // One-shot: the users page opens this user's profile popup once loaded.
  profileToOpen = '';
  pendingMergeListRefreshSiteId = '';
  private deactivatedUserCountSubject = new BehaviorSubject<number>(0);
  public deactivatedUserCount$ =
    this.deactivatedUserCountSubject.asObservable();
  resetFormValue = this.resetForm.asObservable();
  readonly CONSTANTS = CONSTANTS;

  constructor(
    private http: HttpClient,
    private stateManagementService: StateManagementService,
    private translate: TranslateService
  ) {}
  public scrollToTopSubject = new Subject<void>();
  scrollToTop() {
    this.scrollToTopSubject.next();
  }
  getScrollToTopObservable() {
    return this.scrollToTopSubject.asObservable();
  }

  addNewUser(variables: IAddNewUser): Observable<IAddNewUserResponse> {
    return this.http
      .post<any>(this.url, {
        query: `mutation AddNewUser($siteId: String!, $firstName: String!, $middleName: String, $lastName: String!, $email: String, $phoneNumber: String, $notes: String, $isDuplicateNameVerified: Boolean) {
        addNewUser(siteId: $siteId, firstName: $firstName, middleName: $middleName, lastName: $lastName, email: $email, phoneNumber: $phoneNumber, notes: $notes, isDuplicateNameVerified: $isDuplicateNameVerified) {
          email
          stagedEmail
          duplicateEmailUsers { email stagedEmail firstName middleName lastName phoneNumber notes userType isTemporaryUser isAssociatedWithSite }
          duplicateNameUsers { email stagedEmail firstName middleName lastName phoneNumber notes userType isTemporaryUser }
        }
      }`,
        variables,
      })
      .pipe(
        map((response) => {
          if (response.errors?.length) {
            throw new Error(response.errors[0].message);
          }
          if (!response.data?.addNewUser) {
            throw new Error(this.translate.instant('ADD_USER_FAILED'));
          }
          return response.data.addNewUser;
        })
      );
  }

  /**
   * @description this method calls API to share access to user
   * @param { IShareAccess } shareAccessVariables variables of type IShareAccess to give access to a user
   * @returns
   */
  shareAccess(shareAccessVariables: IShareAccess): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `mutation MyMutation($siteId: String!, $email: String, $entities: [Entity]!, $expireTime: String, $firstName: String, $userType: Int, $lastName: String, $phone: String, $repeatDays: [String], $repeatEndTime: String, $repeatStartTime: String, $startTime: String, $existingUserId: String, $userGroups: [String!]) {
          shareAccess(siteId: $siteId, email: $email, entities: $entities, firstName: $firstName, userType: $userType, lastName: $lastName, phone: $phone, startTime: $startTime, expireTime: $expireTime, repeatDays: $repeatDays, repeatEndTime: $repeatEndTime, repeatStartTime: $repeatStartTime, existingUserId: $existingUserId, userGroups: $userGroups) {
          email
          }
          }`,
        variables: shareAccessVariables,
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data?.shareAccess?.email) {
              return res.data.shareAccess;
            } else if (res?.errors?.length > 0) {
              const error = res.errors[0];
              const errorType = error?.errorType;
              const errorMessage =
                typeof error?.message === 'string' ? error.message : '';

              if (errorType === ERROR_CODES.USER_ACCOUNT_DELETION_PENDING) {
                throw new Error(
                  this.translate.instant('USER_REQUESTED_ACCOUNT_DELETION')
                );
              }
              if (errorMessage.includes('phoneNumber')) {
                throw new Error(
                  this.translate.instant('FAILED_TO_SHARE_ACCESS_SERVER_ERROR')
                );
              } else if (errorMessage.includes('same email already exists')) {
                throw new Error(
                  this.translate.instant(
                    'FAILED_EMAIL_ACCOCIATED_EMAIL_ALREADY_ACCESS'
                  )
                );
              } else if (errorMessage.includes('expireTime')) {
                throw new Error(
                  this.translate.instant('FAILED_END_TIME_GREATER_START_TIME')
                );
              } else if (
                errorMessage.includes(
                  'A registered user with same phone number already exists'
                )
              ) {
                throw new Error(
                  this.translate.instant(
                    'FAILED_PHONE_ACCOCIATED_PHONE_ALREADY_ACCESS'
                  )
                );
              } else if (
                errorMessage.includes(
                  this.translate.instant('ONE_OR_MORE_RESOURCES_ALREADY_SHARED')
                )
              ) {
                throw new Error(
                  this.translate.instant('RESOURCE_ALREADY_SHARED')
                );
              } else if (
                errorType === ERROR_CODES.ENTITIES_LIMIT_EXCEEDED ||
                errorMessage.includes(ERROR_CODES.ENTITIES_LIMIT_EXCEEDED)
              ) {
                throw new Error(
                  this.translate.instant('SHARE_ACCESS_ENTITIES_LIMIT_EXCEEDED')
                );
              }
              throw new Error(
                this.translate.instant('FAILED_TO_SHARE_ACCESS_SERVER_ERROR')
              );
            }
            throw new Error(
              this.translate.instant('FAILED_TO_SHARE_ACCESS_SERVER_ERROR')
            );
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to user access group
   * @param { IShareAccess } userGroupAccess variables of type IUpdateUserGroupAccess to will update user access group
   * @returns
   */
  updateUserGroupAccess(
    userGroupAccess: IUpdateUserGroupAccess
  ): Observable<any> {
    let query = '';
    if (userGroupAccess?.expireTime && userGroupAccess?.startTime) {
      query = `mutation ($expireTime: String, $repeatDays: [String], $repeatEndTime: String, $repeatStartTime: String, $startTime: String, $userGroupId: String!, $userId: String!) {
        updateUserGroupAccess(userGroupId: $userGroupId, userId: $userId, expireTime: $expireTime, repeatDays: $repeatDays, repeatEndTime: $repeatEndTime, repeatStartTime: $repeatStartTime, startTime: $startTime) {
          success
        }
      }`;
    } else if (!userGroupAccess?.expireTime && userGroupAccess?.startTime) {
      query = `mutation ($repeatDays: [String], $repeatEndTime: String, $repeatStartTime: String, $startTime: String, $userGroupId: String!, $userId: String!) {
        updateUserGroupAccess(userGroupId: $userGroupId, userId: $userId, repeatDays: $repeatDays, repeatEndTime: $repeatEndTime, repeatStartTime: $repeatStartTime, startTime: $startTime) {
          success
        } 
      }`;
    } else {
      query = `mutation ($repeatDays: [String], $repeatEndTime: String, $repeatStartTime: String, $userGroupId: String!, $userId: String!) {
        updateUserGroupAccess(userGroupId: $userGroupId, userId: $userId, repeatDays: $repeatDays, repeatEndTime: $repeatEndTime, repeatStartTime: $repeatStartTime) {
          success
        }
      }`;
    }
    return this.http
      .post<any>(`${this.url}`, {
        query: query,
        variables: userGroupAccess,
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.updateUserGroupAccess) {
              return res?.data?.updateUserGroupAccess;
            } else if (res?.errors?.length > 0) {
              let errorList = res?.errors.map((x: any) => x.message)[0];
              if (errorList.includes('User is not included in this group.')) {
                throw new Error(
                  this.translate.instant('USER_NOT_INCLUDED_IN_THIS_GROUP);')
                );
              } else if (errorList.includes('expireTime')) {
                throw new Error(
                  this.translate.instant('FAILED_END_TIME_GREATER_START_TIME);')
                );
              }
              throw new Error(
                this.translate.instant('FAILED_TO_SHARE_ACCESS_SERVER_ERROR);')
              );
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls resendInvitationEmail API to resend invitation to a user
   * @param { string } siteId - site id on which the user resides
   * @param { string } email - email of the user to resend the invitation
   * @returns { Observable<boolean> } - true if email sent successfully
   */
  resendInvitationEmail(siteId: string, email: string): Observable<boolean> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `
            mutation resendInvitationEmail($siteId: String!, $email: String!) {
              resendInvitationEmail(siteId: $siteId, email: $email) {
                success
              }
            }
          `,
        variables: {
          siteId,
          email,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.resendInvitationEmail?.success === true) {
            return true;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0].message);
          }
          return false;
        }),
        catchError((err: any) => {
          throw new Error(err?.message);
        })
      );
  }

  /**
   * @description this method calls updatePendingUserPhoneNumber API to correct the phone number of a pending (not yet activated) user
   * @param { string } siteId - site id on which the pending user resides
   * @param { string } email - email of the pending user whose phone number is being corrected
   * @param { string } phoneNumber - new phone number in E.164 format (e.g. +16575686868)
   * @returns { Observable<boolean> } - true if phone number updated successfully
   */
  updatePendingUserPhoneNumber(
    siteId: string,
    email: string,
    phoneNumber: string
  ): Observable<boolean> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `
            mutation updatePendingUserPhoneNumber($siteId: String!, $email: String!, $phoneNumber: String!) {
              updatePendingUserPhoneNumber(siteId: $siteId, email: $email, phoneNumber: $phoneNumber) {
                success
              }
            }
          `,
        variables: {
          siteId,
          email,
          phoneNumber,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.updatePendingUserPhoneNumber?.success === true) {
            return true;
          } else if (res?.errors?.length > 0) {
            // preserve the backend errorType so the caller can map it to a friendly message
            throw new Error(
              res?.errors[0]?.errorType || res?.errors[0]?.message
            );
          }
          return false;
        }),
        catchError((err: any) => {
          throw new Error(err?.message);
        })
      );
  }

  /**
   * @description this method calls API to get all this users to list
   * @param { string } siteId
   * @returns { users }
   */
  getAllUsers(
    siteId: string,
    operationType: string,
    size: number,
    cursor?: string[],
    prepend = false,
    isUserListingTab = false,
    hideLoader: boolean = false,
    searchText: string = '',
    includes?: number[]
  ): Observable<IUser[] | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query ($siteId: String!, $criteria: Criteria, $operationType: String, $includes: [Int]) { getUsersBySite (siteId: $siteId, criteria: $criteria, operationType: $operationType, includes: $includes) { email name phoneNumber userType invitationPending userImgName userImgKey userImgUrl cursor isDeactivated isMobileCredentialEnabled isCardCredentialEnabled isTemporaryUser hasStandardCredential }}`,
          variables: {
            siteId: siteId,
            operationType: operationType,
            includes: includes,
            criteria: {
              size: size,
              cursor: cursor,
              searchText: searchText,
            },
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getUsersBySite) {
              const usersList: IUserSearch[] = res?.data?.getUsersBySite;
              //adding userIds to the users
              usersList.forEach((user: IUser) => {
                user.userId = user?.email!;
                user.userTypeId = user.userType;
                if (userTypeResponse.hasOwnProperty(user.userType)) {
                  user.userType =
                    this.translate.instant(userTypeResponse[user.userType!]) ??
                    user.userType;
                }
              });
              switch (true) {
                case isUserListingTab === true:
                  if (prepend) {
                    this.stateManagementService.prependUsersTabList(usersList);
                  } else {
                    this.stateManagementService.updateUsersTabList(usersList);
                  }
                  break;
                default:
                  if (prepend) {
                    this.stateManagementService.prependUsersList(usersList);
                  } else {
                    this.stateManagementService.updateUsersList(usersList);
                  }
                  break;
              }
              return usersList;
            } else if (res?.errors?.length > 0) {
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_USERS_IN_THIS_SITE')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_USERS_IN_THIS_SITE')
                  );
              }
            }
          } catch (error: any) {
            if (isUserListingTab) {
              this.stateManagementService.updateUsersTabList([]);
            } else {
              this.stateManagementService.updateUsersList([]);
            }
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get all the dasboard users to list
   * @param { string } siteId
   * @returns { users }
   */
  getDashboardUsers(
    criteria: ICriteria,
    hideLoader?: boolean,
    filter?: IFilter[]
  ): Observable<SharedUser | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query ($criteria: Criteria, $filter: [Filter!]) {
                  getUsersByDefaultUserGroup(criteria: $criteria, filter: $filter) {
                      users {
                              unitName
                              buildingName
                              floorName
                              accessPointName
                              startTime
                              expireTime
                              repeatStartTime
                              repeatEndTime
                              repeatDays
                              name
                              userId
                              email
                              userType
                              userImgName
                              userImgKey
                              userImgUrl
                              isExpired
                        
                            },
                            totalCount,
                            cursor
                          }
                      }`,
          variables: {
            criteria,
            filter,
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getUsersByDefaultUserGroup) {
              const usersList: SharedUser =
                res?.data?.getUsersByDefaultUserGroup;

              // Use map to replace userType values with their corresponding values from userTypeResponse
              // Process the users array
              //adding userIds to the users
              usersList?.users.forEach((user: IUser) => {
                user.userId = user?.email!; // Uncomment when API works

                if (userTypeResponse.hasOwnProperty(user.userType)) {
                  user.userType = this.translate.instant(
                    userTypeResponse[user?.userType!]
                  );
                }
              });

              this.stateManagementService.updateDashboardUsersList(usersList);
              return usersList;
            } else if (res?.errors?.length > 0) {
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_USERS_IN_THIS_SITE')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_USERS_IN_THIS_SITE')
                  );
              }
            }
          } catch (error: any) {
            this.stateManagementService.updateDashboardUsersList(undefined);
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get user from user list
   * @param { string } userId
   * @returns { Object } user
   */
  getUserById(userId: string): Observable<IUser | undefined> {
    const usersList: IUser[] = this.stateManagementService.getUsersList();
    const user: IUser | undefined = usersList.find(
      (user) => user?.email === userId
    );
    return new Observable((observer) => {
      observer.next(user);
    });
  }

  /**
   * @description this method delete user from database
   * @param { string } userId
   * @returns { Observable<any> }
   */
  deleteUser(userId: string, siteId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation($userId: String!, $siteId: String!) { deleteUserFromSite(userId: $userId, siteId: $siteId) { userId  }}',
        variables: {
          userId,
          siteId,
        },
      })
      .pipe(
        map((res) => {
          if (res?.data?.deleteUserFromSite) {
            const result = res?.data?.deleteUserFromSite;
            return result;
          } else if (res?.errors?.length > 0) {
            let errorList = res?.errors.map((x: any) => x.message)[0];
            if (
              errorList.includes(ERROR_CODES.USER_NOT_AVAILABLE) ||
              errorList.includes(ERROR_CODES.NOT_AUTHORIZED)
            ) {
              throw new Error(
                this.translate.instant('USER_NOT_AUTHORIZED_TO_DELETE_USER')
              );
            }
            throw new Error(
              this.translate.instant('FAILED_TO_SHARE_ACCESS_SERVER_ERROR')
            );
          }
        }),
        catchError((err: any) => {
          throw new Error(err?.message);
        })
      );
  }

  /**
   * @description this method calls API to get all this users to list
   * @param { string } siteId
   * @returns { users }
   */
  getUsersBySite(
    siteId: string,
    searchedText: string,
    operationType: string,
    prepend = false,
    size: number,
    hideLoader?: boolean,
    filter?: FilterUserParameter[],
    cursor?: string[],
    isUserListingTab: boolean = false,
    group?: IDefaultUserGroup,
    includes?: number[]
  ): Observable<IUser[] | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query ($siteId: String!, $criteria: Criteria, $filter: [Filter!], $operationType: String, $includes: [Int]) { getUsersBySite (siteId: $siteId, criteria: $criteria, filter: $filter, operationType: $operationType, includes: $includes) { email name phoneNumber userType invitationPending userImgName userImgKey userImgUrl cursor groupIds assignedPartnerId isDeactivated isMobileCredentialEnabled isCardCredentialEnabled isTemporaryUser hasStandardCredential }}`,
          variables: {
            siteId: siteId,
            filter: filter,
            operationType: operationType,
            includes: includes,
            criteria: {
              size: size,
              searchText: searchedText,
              cursor: cursor,
            },
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getUsersBySite) {
              const usersList: IUserSearch[] = res?.data?.getUsersBySite;
              usersList.forEach((user: IUser) => {
                user.userId = user?.email!;
                user.userTypeId = user.userType;
                user.status = user.alreadySelected = user.groupIds!.includes(
                  group?.userGroupId!
                );
                if (userTypeResponse.hasOwnProperty(user.userType)) {
                  user.userType =
                    this.translate.instant(userTypeResponse[user.userType!]) ??
                    user.userType;
                }
              });
              switch (true) {
                case isUserListingTab === true:
                  if (prepend) {
                    this.stateManagementService.prependUsersTabList(usersList);
                  } else {
                    this.stateManagementService.updateUsersTabList(usersList);
                  }

                  break;
                default:
                  if (prepend) {
                    this.stateManagementService.prependUsersList(usersList);
                  } else {
                    this.stateManagementService.updateUsersList(usersList);
                  }
                  break;
              }
              return usersList;
            } else if (res?.errors?.length > 0) {
              this.stateManagementService.updateUsersList([]);
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_USERS_IN_THIS_SITE')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_USERS_IN_THIS_SITE')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get user groups
   * @param { string } siteId variables of type string as id for getting user groups
   * @returns
   */
  getUserGroupsBySite(
    siteId: string,
    searchedText?: string,
    hideLoader?: boolean
  ): Observable<any> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query ($siteId: String!, $criteria: Criteria) { getUserGroupsBySite (siteId: $siteId, criteria: $criteria) { userGroupId userGroupName userGroupDescription } }`,
          variables: {
            siteId,
            criteria: {
              searchText: searchedText,
            },
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getUserGroupsBySite) {
              const commonAccessControl: boolean =
                this.stateManagementService.getSharedData()?.selectedSite
                  ?.commonAccessControl;
              if (!commonAccessControl) {
                return res?.data?.getUserGroupsBySite.filter((userGroup: any) =>
                  this.translate
                    .instant('DEFAULT_USER_GROUPS')
                    .includes(userGroup.userGroupName)
                );
              }

              return res?.data?.getUserGroupsBySite;
            } else if (res?.errors?.length > 0) {
              throw new Error(
                this.translate.instant('FAILED_TO_LOAD_USER_GROUPS);')
              );
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get user group info
   * @param { string } userGroupId variables of type string as id for getting user group info
   * @returns
   */
  getUserGroupInfo(userGroupId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `query($userGroupId: String!) { getUserGroupInfo (userGroupId: $userGroupId) {
                    userGroupName, userGroupDescription, userGroupId, siteId, accessPoints, userIds
                  }
                }`,
        variables: {
          userGroupId,
        },
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getUserGroupInfo) {
              return res?.data?.getUserGroupInfo;
            } else if (res?.errors?.length > 0) {
              throw new Error(
                this.translate.instant('FAILED_TO_GET_GROUP_DETAILS);')
              );
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to add user group
   * @param { IUserGroupDetails } variables variables of type IUserGroupDetails to add new user group
   * @returns
   */
  addUserGroup(variables: IUserGroupDetails): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `mutation ($userGroupName:String!, $userGroupDescription:String!, $siteId:String!, $accessPoints: [String!]) { addUserGroup (userGroupName: $userGroupName, userGroupDescription: $userGroupDescription, siteId: $siteId, accessPoints: $accessPoints) { userGroupId } }`,
        variables,
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.addUserGroup) {
              return res?.data?.addUserGroup;
            } else if (res?.errors?.length > 0) {
              throw new Error(
                this.translate.instant('FAILED_TO_ADD_USER_GROUP);')
              );
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to add user group
   * @param { IUserGroupDetails } variables variables of type IUserGroupDetails to add new user group
   * @returns
   */
  updateUserGroup(
    variables: IUserGroupUpdateDetails
  ): Observable<IUserGroupDetails> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `mutation($userGroupName:String!, $userGroupId:String!, $userGroupDescription:String!, $deleteAccessPoints:[AccessPointIdInput!], $addAccessPoints:[AccessPointIdInput!]){
          updateUserGroup(userGroupName: $userGroupName, userGroupId: $userGroupId, userGroupDescription: $userGroupDescription, deleteAccessPoints: $deleteAccessPoints, addAccessPoints: $addAccessPoints){
            userGroupId
          }
        }`,
        variables,
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.updateUserGroup) {
              return res?.data?.updateUserGroup;
            } else if (res?.errors?.length > 0) {
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.USER_GROUP_ALREADY_EXISTS:
                  throw new Error(
                    this.translate.instant('GROUP_NAME_ALREADY_EXISTS')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_UPDATE_USER_GROUP')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to add user group
   * @param { string } groupId groupId of type string to delete group
   * @returns
   */
  deleteGroup(userGroupId: string): Observable<IUserGroupDetails> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `mutation ($userGroupId: String!) { deleteUserGroup(userGroupId: $userGroupId) { userGroupId }}`,
        variables: {
          userGroupId,
        },
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.deleteUserGroup) {
              return res?.data?.deleteUserGroup;
            } else if (res?.errors?.length > 0) {
              throw new Error(
                this.translate.instant('FAILED_TO_DELETE_USER_GROUP')
              );
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  verifyUser(email: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `query($email: String!) { getUserSummary(email: $email) {
        firstName,lastName,phoneNumber,userType, isDeactivated
                }
              }`,
        variables: {
          email,
        },
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getUserSummary) {
              return res;
            } else if (res?.errors?.length > 0) {
              let errorList = res?.errors.map((x: any) => x.message)[0];
              if (
                errorList.includes(this.translate.instant('USER_NOT_AVAILABLE'))
              ) {
                res.errors = this.translate.instant('USER_NOT_AVAILABLE');
                return res;
              } else if (errorList.includes('authorized')) {
                throw new Error(
                  this.translate.instant(
                    'PMU_CANNOT_MODIFY_PMA_PMU_USERS_DETAILS'
                  )
                );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to add user group
   * @param { IUserGroupDetails } variables variables of type IUserGroupDetails to add new user group
   * @returns
   */
  addUsersToUserGroup(variables: any, loader?: boolean): Observable<any> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `mutation ($expireTime: String, $repeatDays: [String], $repeatEndTime: String, $repeatStartTime: String, $siteId: String!, $startTime: String, $userGroupId: String!, $users: [UserProps!]!){
                  addUsersToUserGroup (siteId: $siteId, startTime: $startTime, userGroupId: $userGroupId, users: $users, expireTime: $expireTime, repeatDays: $repeatDays, repeatEndTime: $repeatEndTime, repeatStartTime: $repeatStartTime) {
                    userGroupId
                  }
                }`,
          variables,
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.addUsersToUserGroup) {
              return res?.data?.addUserGroup;
            } else if (res?.errors?.length > 0) {
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.USER_ALREADY_EXISTS:
                  throw new Error(
                    this.translate.instant('USER_ALREADY_EXISTS')
                  );
                case ERROR_CODES.INVALID_ACCESS_TIME:
                  throw new Error(
                    this.translate.instant(
                      'END_TIME_SHOULD_BE_GREATER_THAN_START_TIME'
                    )
                  );
                case ERROR_CODES.USER_ACCOUNT_DELETION_PENDING:
                  throw new Error(
                    this.translate.instant('USER_REQUESTED_ACCOUNT_DELETION')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_ADD_USER_TO_USER_GROUP')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }
  /**
   * @description this method set the value of triggerRerender which runs the subscription to re-render UI of users list
   * @param { boolean } value toggle boolean to re-render user list when update in user image and user name occurs
   */
  setTriggerRerender(value: boolean) {
    this.triggerRerender.next(value);
  }

  /**
   * @description sets the pending deletions count (source of truth for the Users panel)
   */
  setDeactivatedUserCount(count: number): void {
    this.deactivatedUserCountSubject.next(Math.max(0, count ?? 0));
  }

  /**
   * @description optimistically decrements the pending deletions count for instant UI update
   * @param decrementAmount number of pending deletions removed (default 1)
   */
  triggerDeactivatedUserCountRefresh(decrementAmount: number = 1): void {
    const next = Math.max(
      0,
      this.deactivatedUserCountSubject.value - decrementAmount
    );
    this.deactivatedUserCountSubject.next(next);
  }

  /**
   * @description getUserAccess method calls API to get user access
   * @param { string } siteId variables of type string as site id
   * @param { string } userId variables of type string as user id
   * @returns
   */
  getUserAccess(
    siteId: String | null | undefined,
    userId: string
  ): Observable<any> {
    const commonAccessControl: boolean =
      this.stateManagementService.getSharedData()?.selectedSite
        ?.commonAccessControl;

    return this.http
      .post<any>(`${this.url}`, {
        query: `query ($userId: String!, $siteId: String!) {
          getUserAccess(siteId: $siteId, userId: $userId) {
            unitsAndAccessPoints {
              buildingId
              accessPoints {
                accessPointId
                isActive
              }
              units {
                locks {
                  isActive
                  lockId
                }
                isCompleteAccess
                isActive
                unitId
              }
            }
            userGroups
          }   
        }`,
        variables: {
          siteId,
          userId,
        },
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getUserAccess) {
              if (!commonAccessControl) {
                res.data.getUserAccess.unitsAndAccessPoints =
                  res.data.getUserAccess.unitsAndAccessPoints.map(
                    (item: any) => ({
                      ...item,
                      accessPoints: [],
                    })
                  );
              }

              return res?.data?.getUserAccess;
            } else if (res?.errors?.length > 0) {
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.USER_TYPE_NOT_ALLOWED:
                  throw new Error(
                    this.translate.instant(
                      'SHARE_ACCESS_TO_SPECIFIC_UNIT_NOT_ALLOWED'
                    )
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_GROUP_DETAILS')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  rejectAccountDeletionRequest(
    userId: string,
    siteId: string
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation RejectAccountDeletionRequest($userId: String!, $siteId: String!) { rejectAccountDeletionRequest(userId: $userId, siteId: $siteId) { userId } }',
        variables: {
          userId,
          siteId,
        },
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data?.rejectAccountDeletionRequest) {
              this.triggerDeactivatedUserCountRefresh(1);
              return res.data.rejectAccountDeletionRequest;
            } else if (res?.errors?.length > 0) {
              throw new Error(
                this.translate.instant('FAILED_TO_REJECT_DELETION')
              );
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get deactivated user count
   * @param { string } siteId variables of type string as id for getting deactivated user count
   * @returns Observable<number>
   */
  getDeactivatedUserCount(
    siteId: string,
    hideLoader?: boolean
  ): Observable<number> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query ($siteId: String!) { getDeactivatedUserCount (siteId: $siteId) { count } }`,
          variables: {
            siteId,
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getDeactivatedUserCount) {
              return res.data.getDeactivatedUserCount.count;
            } else if (res?.errors?.length > 0) {
              throw new Error(
                this.translate.instant('FAILED_TO_LOAD_DEACTIVATED_USER_COUNT')
              );
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }
}
